import React, { useMemo, useState } from 'react';
import { useGetShopProductsQuery } from 'graphql/generated/remote-schema-hasura';
import ModalComponent from 'components/modal/modalComponent';
import EditQuantityCell from 'components/Shop/EditableCell';
import { twMerge } from 'tailwind-merge';
import { Product, productDataMock } from './useRecommendedProducts';
import { genericActionsIds } from 'utilities/constants';
import { useGetGenericActions } from 'hooks/useGetGenericActions';
import { ProductCategory } from '../../../../shop';

interface IRecommendedProductModalProps {
  isOpen: boolean;
  onClose: () => void;
  patientFullName?: string;
  handleSaveProduct: () => Promise<void> | void;
  handleCancelSaveProduct: () => void;
  locale: Record<string, string>;
  selectedProducts: Record<string, number>;
  updateSelectedProducts: React.Dispatch<
    React.SetStateAction<Record<string, number>>
  >;
  submitButtonText?: string;
}

const RecommendedProductModal: React.FC<IRecommendedProductModalProps> = ({
  isOpen,
  onClose,
  patientFullName,
  locale,
  selectedProducts,
  updateSelectedProducts,
  handleSaveProduct,
  handleCancelSaveProduct,
  submitButtonText,
}) => {
  const { data, loading } = useGetShopProductsQuery({
    fetchPolicy: 'cache-first',
    variables: { limit: 10, offset: 0, categories: ProductCategory.TestKit },
  });

  const [isLoading, setIsLoading] = useState(false);

  const products = useMemo(
    () => data?.getProducts.products || productDataMock.products,
    [data],
  );

  const { data: genericActions, loading: genericActionsLoading } =
    useGetGenericActions({
      locale: 'en',
      genericActionId: [genericActionsIds.CANCEL, genericActionsIds.CONFIRM],
    });

  const COLUMNS = [
    {
      header: locale?.nameColumn,
      className: 'text-left',
      cellClassName:
        'desktop:text-granite-grey text-btn-black desktop:font-medium font-semibold flex-[0_0_100%] desktop:table-cell',
      accessor: 'title',
    },
    {
      header: locale?.quantityColumn,
      className: 'text-center w-[150px]',
      cellClassName: 'order-1 w-1/2 desktop:w-fit desktop:table-cell',
    },
  ];

  const BUTTONS = useMemo(
    () => [
      {
        label: submitButtonText || locale?.addRecommendationBtn,
        onClick: async () => {
          setIsLoading(true);
          await handleSaveProduct();
          setIsLoading(false);
          onClose();
        },
        disabled:
          !Object.values(selectedProducts).some((value) => value > 0) ||
          isLoading,
        type: 'filled',
        className: 'text-sm',
        testID: 'add-recommendation',
        showSpinner: isLoading,
      },
      {
        label: genericActions?.[genericActionsIds.CANCEL].cancel,
        disabled: false,
        onClick: () => {
          handleCancelSaveProduct();
          onClose();
        },
        type: 'underline',
        className: 'text-sm',
        testID: 'close',
      },
    ],
    [
      submitButtonText,
      locale?.addRecommendationBtn,
      selectedProducts,
      isLoading,
      genericActions,
      handleSaveProduct,
      onClose,
      handleCancelSaveProduct,
    ],
  );

  if (loading || genericActionsLoading) return null;

  return (
    <ModalComponent
      onClose={onClose}
      isOpen={isOpen}
      title={locale?.recommendedModalTitle}
      contentPaddingY="desktop:py-5 pb-0 pt-5"
      buttons={BUTTONS}
    >
      <div className="desktop:gap-30 flex flex-col">
        <div>{`${locale?.recommendedModalSubtitle} ${patientFullName}.`}</div>

        <table className={'table-auto w-full'}>
          <thead className="bg-base-content hidden desktop:table-header-group">
            <tr className="border-b">
              {COLUMNS.map(({ header, className }, index) => (
                <th
                  className={twMerge(
                    'text-base text-charcoal-gray p-2.5',
                    className,
                  )}
                  key={index}
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {products.map((dataObject, index) => (
              <tr
                className={twMerge(
                  'border-b p-5 desktop:p-0 flex desktop:table-row flex-wrap',
                  'desktop:gap-y-0 items-center',
                  'gap-y-1 last:border-b-0',
                )}
                key={index}
              >
                <td
                  className={twMerge(
                    'p-0 desktop:py-4 desktop:px-2.5',
                    COLUMNS[0].cellClassName,
                  )}
                  key={`${index}_1`}
                >
                  {dataObject[COLUMNS[0].accessor as keyof Product]}
                </td>
                <td
                  key={`editable_${index}_2`}
                  className={COLUMNS[1].cellClassName}
                >
                  <EditQuantityCell
                    currentQuantity={selectedProducts[dataObject.id] ?? 0}
                    increase={() => {
                      updateSelectedProducts((prevState) => {
                        return {
                          ...prevState,
                          [dataObject.id]: (prevState[dataObject.id] ?? 0) + 1,
                        };
                      });
                    }}
                    decrease={() => {
                      updateSelectedProducts((prevState) => {
                        return {
                          ...prevState,
                          [dataObject.id]: (prevState[dataObject.id] ?? 0) - 1,
                        };
                      });
                    }}
                    onChange={(amount) => {
                      updateSelectedProducts((prevState) => {
                        return {
                          ...prevState,
                          [dataObject.id]: amount,
                        };
                      });
                    }}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </ModalComponent>
  );
};

export default RecommendedProductModal;
